"use client";

import Link from "next/link";
import { useMemo, useState } from "react";
import { LuExternalLink, LuInfo } from "react-icons/lu";

import CoinPriceFormatter from "@/components/shared/coin-price-formatter";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import useAmmOrderbook from "@/hooks/use-amm-orderbook";
import { type CoinPropertiesResponse } from "@/hooks/use-coin-properties-query";
import {
  DESO_APP_NAME,
  FOCUS_TOKEN_DISPLAY_NAME,
  OPENFUND_APP_NAME,
} from "@/utils/constants";
import {
  basisPointsToPercent,
  formatDecimalValue,
  getVisiblePrice,
} from "@/utils/currency";

import { LearnMore } from "../learn-more-link";
import AmmSummary from "../trade/amm/amm-summary";
import AmmSummaryGraph from "../trade/amm/amm-summary-graph";
import TokenOrderbook, { OrderDirection } from "../trade/amm/token-orderbook";

interface TokenPreLaunchAmmProps {
  tokenDetails: CoinPropertiesResponse;
  coinPublicKey: string;
  focusExchangeRate: number;
  desoExchangeRate: number;
}

const TokenPreLaunchAmm = ({
  tokenDetails,
  coinPublicKey,
  focusExchangeRate,
  desoExchangeRate,
}: TokenPreLaunchAmmProps) => {
  const [showInfo, setShowInfo] = useState(false);

  const tokenPriceUsd = tokenDetails?.coinPrice
    ? getVisiblePrice(tokenDetails.coinPrice)
    : 0;

  const { loadingAmmOrderbook, ammOrderbook } = useAmmOrderbook({
    publicKey: coinPublicKey,
    tokenPriceUsd,
    focusExchangeRate,
    desoExchangeRate,
    transactorPublicKeyBase58Check:
      tokenDetails.coinProperties.AmmConfigs?.[0]?.AmmPublicKey || "",
  });

  const ammConfig = tokenDetails?.coinProperties.AmmConfigs?.length
    ? tokenDetails?.coinProperties.AmmConfigs[0]
    : null;

  const reversedAmmOrderbook = useMemo(() => {
    return [
      ...(ammOrderbook?.asks || []),
      ...(ammOrderbook?.bids || []),
    ].reverse();
  }, [ammOrderbook]);

  const renderAmmSummary = () => {
    if (!ammConfig || !reversedAmmOrderbook.length) {
      return null;
    }

    const config = [
      {
        title: "Start Price (USD)",
        value: <CoinPriceFormatter price={ammConfig.StartPriceUsd} />,
        key: "startPriceUsd",
      },
      {
        title: "Order Spacing",
        value:
          formatDecimalValue(
            basisPointsToPercent(ammConfig.OrderSpacingBasisPoints),
          ) + "%",
        key: "orderSpacingPercentage",
      },
      {
        key: "baseAmountPerLevelUsd",
        value: <CoinPriceFormatter price={ammConfig.BaseAmountPerLevelUsd} />,
        title: "Amount Per Level USD",
      },
      {
        key:
          ammConfig.IncreaseBaseAmountByUsdEachLevel > 0
            ? "increaseBaseAmountByUsdEachLevel"
            : "increaseBaseAmountByBasisPointsEachLevel",
        value:
          ammConfig.IncreaseBaseAmountByUsdEachLevel > 0 ? (
            <CoinPriceFormatter
              price={ammConfig.IncreaseBaseAmountByUsdEachLevel}
              maxDecimalsDigitsAfterZero={4}
            />
          ) : (
            formatDecimalValue(
              basisPointsToPercent(
                ammConfig.IncreaseBaseAmountByBasisPointsEachLevel,
              ),
            ) + "%"
          ),
        title:
          ammConfig.IncreaseBaseAmountByUsdEachLevel > 0
            ? "Amount Increase by USD"
            : "Amount Increase Per Level",
      },
      {
        key: "terminalPriceUsd",
        value: !ammConfig.TerminalPriceUsd ? (
          <div className="inline-block font-mono text-muted-foreground">
            &mdash;
          </div>
        ) : (
          <CoinPriceFormatter price={ammConfig.TerminalPriceUsd} />
        ),
        title: "Terminal Price USD",
      },
      {
        key: "terminalAmountUsd",
        value: !ammConfig.TerminalAmountUsd ? (
          <div className="inline-block font-mono text-muted-foreground">
            &mdash;
          </div>
        ) : (
          <CoinPriceFormatter price={ammConfig.TerminalAmountUsd} />
        ),
        title: "Terminal Amount USD",
      },
      {
        key: "finalPriceUsd",
        value: !ammConfig.FinalPriceUsd ? (
          <div className="inline-block font-mono text-muted-foreground">
            &mdash;
          </div>
        ) : (
          <CoinPriceFormatter price={ammConfig.FinalPriceUsd} />
        ),
        title: "Final Price USD",
      },
    ];

    return (
      <AmmSummary
        ammConfig={config}
        orders={reversedAmmOrderbook}
        coinUsername={tokenDetails.coinProperties.Profile.Username}
      />
    );
  };

  if (loadingAmmOrderbook) {
    return (
      <div className="flex flex-col gap-2">
        <Skeleton className="h-[300px] w-full" />
        <Skeleton className="h-[315px] w-full" />
        <Skeleton className="h-[400px] w-full" />
      </div>
    );
  }

  return (
    <div>
      <div className="mb-4">
        <div className="mb-4 flex items-center gap-1">
          <h2
            className="
            font-sans text-base font-semibold text-muted-foreground
          "
          >
            AMM Summary
          </h2>
          <Button
            variant="link"
            onClick={() => setShowInfo(!showInfo)}
            className="
              flex items-center gap-2 text-xs text-muted underline-offset-2
              transition-colors
              hover:text-muted-foreground hover:underline
            "
          >
            How it works
            <LuInfo className="text-lg text-muted" />
          </Button>
        </div>

        {showInfo && (
          <div
            className="
              animate-in space-y-4 rounded-2xl border p-6 text-sm text-muted
              duration-700 fade-in
            "
          >
            <p>
              These are the initial orders that the Automated Market-Maker (AMM)
              will place to initialize the market for this token.
            </p>

            <p>
              Whenever a sell order fills, the AMM places a buy order on the
              other side, and vice-versa, so there's always liquidity for buyers
              and sellers.
            </p>

            <p>
              Before the market opens, anyone can place a bid to reserve tokens
              at the lowest possible price, including you! Bids will be filled
              by the AMM's sell orders when it starts.
            </p>

            <p>
              All orders are fully on-chain thanks to the {DESO_APP_NAME}{" "}
              blockchain, and you can see the full market and place on-chain
              limit orders via the {OPENFUND_APP_NAME} client. And yes, order
              matching is fully on-chain too!
            </p>

            <p>
              <LearnMore />
            </p>
          </div>
        )}
      </div>

      <AmmSummaryGraph
        orders={reversedAmmOrderbook}
        graphOptions={{
          stroke: {
            curve: "step",
            width: 2,
          },
          fill: {
            type: "gradient",
            gradient: {
              shade: "dark",
              type: "vertical",
              shadeIntensity: 0.2,
              opacityFrom: 0.1,
              opacityTo: 0.6,
              stops: [0, 100],
            },
          },
          yaxis: {
            forceNiceScale: true,
            title: {
              text: "Token Price USD",
              style: { color: "#fff" },
            },
          },
          xaxis: {
            title: {
              text: "Token Amount",
              style: { color: "#fff" },
            },
          },
          colors: ["#22c55d", "#ef4444"],
        }}
        coinUsername={tokenDetails.coinProperties.Profile.Username}
      />

      <div className="mb-4">{renderAmmSummary()}</div>

      <div className="mb-4 flex items-center justify-between">
        <h2 className="font-sans text-base font-semibold text-muted-foreground">
          AMM Orders
        </h2>
        <Link
          href={`${process.env.NEXT_PUBLIC_OPENFUND_URI}/trade/${tokenDetails.coinProperties.Profile.Username}?quote=${FOCUS_TOKEN_DISPLAY_NAME}`}
          target="_blank"
        >
          <Button
            key="trade-on-openfund"
            className="flex items-center gap-2"
            variant="outline"
            size="sm"
          >
            Trade on {OPENFUND_APP_NAME}
            <LuExternalLink className="text-muted" />
          </Button>
        </Link>
      </div>

      <div className="pb-0">
        <TokenOrderbook
          orders={reversedAmmOrderbook}
          direction={OrderDirection.topToBottom}
          coinUsername={tokenDetails.coinProperties.Profile.Username}
          coinPublicKey={
            tokenDetails.coinProperties.Profile.PublicKeyBase58Check
          }
          tableClassName="min-w-[670px]"
          defaultTransactor={{
            publicKey: "",
            username: `AMM_${tokenDetails.coinProperties.Profile.Username}`,
            extraData: {
              DisplayName: "Automated Market Maker",
            },
          }}
        />
      </div>
    </div>
  );
};

export default TokenPreLaunchAmm;
